export type CalcToken =
  | { kind: 'number'; value: number; raw: string }
  | { kind: 'op'; value: '+' | '-' | '*' | '/' }
  | { kind: 'lparen' }
  | { kind: 'rparen' }
  | { kind: 'percent' };

export class CalculatorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CalculatorError';
  }
}

function normalizeExpression(input: string): string {
  return input
    .replace(/×/g, '*')
    .replace(/÷/g, '/')
    .replace(/−/g, '-')
    .replace(/\s+/g, '');
}

export function tokenize(expression: string): CalcToken[] {
  const src = normalizeExpression(expression);
  if (!src) throw new CalculatorError('Empty expression');

  const tokens: CalcToken[] = [];
  let i = 0;

  while (i < src.length) {
    const ch = src[i];

    if ((ch >= '0' && ch <= '9') || ch === '.') {
      let j = i + 1;
      let sawDot = ch === '.';
      while (j < src.length) {
        const next = src[j];
        if (next >= '0' && next <= '9') {
          j += 1;
          continue;
        }
        if (next === '.' && !sawDot) {
          sawDot = true;
          j += 1;
          continue;
        }
        break;
      }
      const raw = src.slice(i, j);
      if (raw === '.' || raw === '') throw new CalculatorError('Invalid number');
      const value = Number(raw);
      if (!Number.isFinite(value)) throw new CalculatorError('Invalid number');
      tokens.push({ kind: 'number', value, raw });
      i = j;
      continue;
    }

    if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
      tokens.push({ kind: 'op', value: ch });
      i += 1;
      continue;
    }
    if (ch === '(') {
      tokens.push({ kind: 'lparen' });
      i += 1;
      continue;
    }
    if (ch === ')') {
      tokens.push({ kind: 'rparen' });
      i += 1;
      continue;
    }
    if (ch === '%') {
      tokens.push({ kind: 'percent' });
      i += 1;
      continue;
    }

    throw new CalculatorError(`Unexpected character: ${ch}`);
  }

  return tokens;
}

type RpnItem =
  | { kind: 'number'; value: number }
  | { kind: 'op'; value: '+' | '-' | '*' | '/' | 'u-' | '%' };

function precedence(op: RpnItem & { kind: 'op' }): number {
  if (op.value === 'u-' || op.value === '%') return 3;
  if (op.value === '*' || op.value === '/') return 2;
  return 1;
}

function isRightAssociative(op: RpnItem & { kind: 'op' }): boolean {
  return op.value === 'u-' || op.value === '%';
}

/** Shunting-yard to RPN with unary minus and postfix percent. */
export function toRpn(tokens: CalcToken[]): RpnItem[] {
  const output: RpnItem[] = [];
  const stack: Array<RpnItem | { kind: 'lparen' }> = [];
  let expectOperand = true;

  for (const token of tokens) {
    if (token.kind === 'number') {
      output.push({ kind: 'number', value: token.value });
      expectOperand = false;
      continue;
    }

    if (token.kind === 'percent') {
      if (expectOperand) throw new CalculatorError('Unexpected %');
      const op: RpnItem = { kind: 'op', value: '%' };
      while (stack.length) {
        const top = stack[stack.length - 1];
        if (top.kind !== 'op') break;
        const shouldPop =
          precedence(top) > precedence(op) ||
          (precedence(top) === precedence(op) && !isRightAssociative(op));
        if (!shouldPop) break;
        output.push(stack.pop() as RpnItem);
      }
      stack.push(op);
      expectOperand = false;
      continue;
    }

    if (token.kind === 'op') {
      let opValue: '+' | '-' | '*' | '/' | 'u-' = token.value;
      if (token.value === '-' && expectOperand) opValue = 'u-';
      else if (expectOperand && token.value !== '-') {
        throw new CalculatorError('Unexpected operator');
      }
      const op: RpnItem = { kind: 'op', value: opValue };
      while (stack.length) {
        const top = stack[stack.length - 1];
        if (top.kind !== 'op') break;
        const shouldPop =
          precedence(top) > precedence(op) ||
          (precedence(top) === precedence(op) && !isRightAssociative(op));
        if (!shouldPop) break;
        output.push(stack.pop() as RpnItem);
      }
      stack.push(op);
      expectOperand = true;
      continue;
    }

    if (token.kind === 'lparen') {
      stack.push({ kind: 'lparen' });
      expectOperand = true;
      continue;
    }

    // rparen
    let found = false;
    while (stack.length) {
      const top = stack.pop()!;
      if (top.kind === 'lparen') {
        found = true;
        break;
      }
      output.push(top);
    }
    if (!found) throw new CalculatorError('Mismatched parentheses');
    expectOperand = false;
  }

  while (stack.length) {
    const top = stack.pop()!;
    if (top.kind === 'lparen') throw new CalculatorError('Mismatched parentheses');
    output.push(top);
  }

  return output;
}

export function evalRpn(items: RpnItem[]): number {
  const stack: number[] = [];
  for (const item of items) {
    if (item.kind === 'number') {
      stack.push(item.value);
      continue;
    }
    if (item.value === 'u-') {
      if (stack.length < 1) throw new CalculatorError('Invalid expression');
      stack.push(-stack.pop()!);
      continue;
    }
    if (item.value === '%') {
      if (stack.length < 1) throw new CalculatorError('Invalid expression');
      stack.push(stack.pop()! / 100);
      continue;
    }
    if (stack.length < 2) throw new CalculatorError('Invalid expression');
    const b = stack.pop()!;
    const a = stack.pop()!;
    let result = 0;
    switch (item.value) {
      case '+':
        result = a + b;
        break;
      case '-':
        result = a - b;
        break;
      case '*':
        result = a * b;
        break;
      case '/':
        if (b === 0) throw new CalculatorError('Division by zero');
        result = a / b;
        break;
    }
    if (!Number.isFinite(result)) throw new CalculatorError('Invalid result');
    stack.push(result);
  }
  if (stack.length !== 1) throw new CalculatorError('Invalid expression');
  return stack[0];
}

export function formatCalcNumber(value: number, maxFractionDigits = 10): string {
  if (!Number.isFinite(value)) return 'Error';
  if (Object.is(value, -0)) return '0';
  const abs = Math.abs(value);
  if ((abs !== 0 && abs < 1e-9) || abs >= 1e12) {
    return value.toExponential(6).replace(/\.?0+e/, 'e');
  }
  const rounded = Number(value.toPrecision(12));
  return String(parseFloat(rounded.toFixed(maxFractionDigits)));
}

export function evaluateExpression(expression: string): number {
  const tokens = tokenize(expression);
  const rpn = toRpn(tokens);
  return evalRpn(rpn);
}

export function tryEvaluateExpression(expression: string): { ok: true; value: number } | { ok: false; error: string } {
  try {
    return { ok: true, value: evaluateExpression(expression) };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Invalid expression' };
  }
}
